import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewRef,
} from '@angular/core';
import { Commands, Results, Search, Style } from '@local/client-contracts';
import { Constants, ManualPromise } from '@local/common';
import { isEmbed, isMac } from '@local/common-web';
import { KeyName, getOSShortcut, isIconEmoji } from '@local/ts-infra';
import { InputTitleComponent, PopupService, TelemetryTarget, UInputComponent, UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { DragData, DragMetadata } from '@shared/directives/drag-and-drop.directive';
import { EventInfo, EventsService, LogService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { CustomKeyboardEvent, KeyboardService, Shortcuts } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import {
  getShareAssetsPath,
  isChangeFavoriteStatusCommand,
  isMiddleClick,
  isOpenFileCommand,
  isOpenUrlCommand,
  isRightClick,
  isShareCommand,
  windowSizeObserver,
} from '@shared/utils';
import { getIconModel } from '@shared/utils/set-icon.util';
import { Bullet } from '@unleash-tech/apps-client-sdk';
import { Logger } from '@unleash-tech/js-logger';
import { cloneDeep, isEqual } from 'lodash';
import { ReplaySubject, Subject, Subscription, filter } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ResultTagComponent } from 'src/app/bar/components/result-tag/result-tag.component';
import { ShareCommand } from 'src/app/bar/models/share-command';
import { WorkspacesService } from 'src/app/bar/services';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import {
  ContextMenu,
  ContextMenuInvokeSource,
  ResultsItemContextMenuHelper,
} from 'src/app/bar/services/commands/results-item-context-menu-helper';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import * as uuid from 'uuid';
import { GlobalErrorHandler } from '../../../../global-error-handler';
import { CommandsService } from '../../../services/commands/commands.service';
import { PreviewMode } from '../../preview/model/preview-mode';
import { ResultAttachmentComponent } from '../../results/components/result-attachment/result-attachment.component';
import { ResultBulletComponent } from '../../results/components/result-bullet/result-bullet.component';
import { InvokeCommand } from '../../results/models/invoke-command.model';
import {
  DisplayItemData,
  ResultContextMenuItem,
  ResultContextMenuItemId,
  SearchResults,
  TelemetryTrigger,
} from '../../results/models/results-types';
import { Action, PageType } from '../../results/models/view-filters';
import { ViewItem } from '../../results/models/view-item';
import { ResultCommandBuilder, ResultCommandBuilderParams } from '../../results/services/result-command-builder.service';
import {
  convertResultTypeToFavoriteType,
  getPrimaryActionClick,
  isAttachment,
  isEnabledSummary,
  isPreviewClickAction,
  isWikiCard,
  isWikiItem,
} from '../../results/utils/results.util';
import { SearchPopupItem } from '../../special-views/search-popup/model';
import { SearchPopUpService } from '../../special-views/search-popup/search-popup.service';

export type onChangeMode = 'model' | 'selected';
export interface ShortcutHandler {
  fn: (item: any, trigger: TelemetryTrigger, eventInfo?: Partial<EventInfo>) => any;
  keyBinding?: KeyName[];
}

export interface ShareItem {
  iconUrl: string;
  id: string;
  text: string;
  url: string;
}

export interface EventResource {
  id: string;
  list: string;
  appId?: any;
  type: 'result' | 'go-link' | 'collection-url' | 'collection-header' | 'collection-file' | 'collections' | 'external-source';
  position: number;
  linkId?: any;
}

export type viewTagGroup = { tags: Array<Results.Attachment | Results.Tag>; icon: UiIconModel };

@UntilDestroy()
@Component({
  selector: 'view-item',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class ViewItemComponent<T extends Search.ResultItem> implements OnInit, ViewItem, OnDestroy {
  private originalModel: T;
  protected showResultSectionsDefault: Results.ShowResultSections = {
    showBullets: true,
    showContextMenu: true,
    showTags: true,
    enableCommandBar: true,
    showSnippet: true,
    showAnnotation: true,
    showSubtitleInSecondLine: false,
    showSubtitle: true,
    hasResource: true,
    showSubView: true,
    showPreviewIcon: true,
    showSummaryIcon: true,
    showPreviewIconContextMenu: true,
    showLinkIndication: true,
    showAddToCollectionAction: true,
    showFavoriteContextMenuItem: true,
    showAddToCollectionContextMenuItem: true,
    showLinkIndicator: true,
    showShareAction: true,
    showSummaryAction: true,
    addCalcBulletsObserver: false,
  };
  isShowSingleLineView: boolean;
  static shortcutsHandlers: any;
  @Input() set model(value: T) {
    if (isEqual(this.originalModel, value)) {
      return;
    }
    this.originalModel = cloneDeep(value);
    this.prevModel = this._model;
    if (value?.id != this._model?.id) {
      this.favoriteOverride = null;
    }
    if (this.favoriteOverride !== null) {
      value.isFavorite = this.favoriteOverride;
    }
    this._model = value;
    this.unregisterFromCommandBar();
    if (!isEqual(this._model, this.prevModel)) {
      this.onChanges('model');
    }
    this.isShowSingleLineView = value?.showResultSections?.showSingleLineView || this.showResultSections?.showSingleLineView;
    this.showResultSections = {
      ...this.showResultSections,
      showInputTitle: value?.showResultSections?.showInputTitle,
      isEditable: value?.showResultSections?.isEditable,
      showLinkIndication: value?.showResultSections?.showLinkIndication,
      showSingleLineView: this.isShowSingleLineView,
    };
    this.updateThumbnailUrl();
    this.onChangeModel();
  }
  @Input() set selected(value: boolean) {
    this.prevSelected = this._selected;
    this._selected = value;
    if (this.isSelectionChanged()) {
      this.onChanges('selected');
    }
    if (!this._selected && this.confirmOptions) {
      this.onConfirm(false);
    }
  }
  @Input() sessionId: string;
  @Input() searchId: string;
  @Input() clientSearchId: string;
  @Input() query: string;
  @Input() index: number;
  @Input() resultsIndex: number;
  @Input() displayOneLine = false;
  @Input() listName: string;
  @Input() isLauncher: boolean;
  @Input() resourceList: string;
  @Input() pageType: PageType;
  @Input() previewable: boolean;
  @Input() actionsReadOnly: boolean;
  @ViewChild('subtitle', { read: ElementRef }) subtitleRef: ElementRef;
  @ViewChild('title', { read: ElementRef }) titleRef: ElementRef;
  @ViewChild('actions', { read: ElementRef }) actionsRef: ElementRef;
  @ViewChild('resultBullets', { read: ElementRef }) resultBulletsRef: ElementRef;
  @ViewChild('resultTags', { read: ElementRef }) resultTagsRef: ElementRef;
  @ViewChildren('resultAttachment', { read: ResultAttachmentComponent }) attachmentsRef: QueryList<ResultAttachmentComponent>;
  @ViewChild(InputTitleComponent) inputTitleComponent: InputTitleComponent;
  @ViewChild('main', { read: ElementRef }) mainRef: ElementRef;
  @ViewChildren('resultBullet', { read: ResultBulletComponent }) bulletPartsRef: QueryList<ResultBulletComponent>;
  @ViewChildren('resultTag', { read: ResultTagComponent }) tagsRef: QueryList<ResultTagComponent>;
  @ViewChild('snippet', { read: ElementRef }) snippetRef: ElementRef;
  @Output() contextOpen = new EventEmitter();
  @Output() contextClose = new EventEmitter();
  @Output() invoke = new EventEmitter<InvokeCommand>();
  @Output() titleChanged = new EventEmitter<string>();
  @Output() invokeSelectedIndex = new EventEmitter<void>();
  isEmbed: boolean = isEmbed();
  protected isContainerItem: boolean = false;
  protected _previewState: PreviewMode;
  @Input() set previewState(val: PreviewMode) {
    this._previewState = val;
  }

  get previewState(): PreviewMode {
    return this._previewState;
  }
  private _showResultSections: Results.ShowResultSections;
  @Input() set showResultSections(value: Results.ShowResultSections) {
    this.setShowResultSections(value);
  }
  @Input() set resultsWidth(val: number) {
    const prevWidth = this.mainWidth;
    this.onWidthItemChange(prevWidth);
  }

  @Input() set contextMenu(val: ContextMenu) {
    if (!val || this.model?.id !== val.itemId) return;
    this.openContextMenu(val.event, val.source);
  }

  private _layoutMode: Results.LayoutType;
  @Input() set layoutMode(val: Results.LayoutType) {
    if (val === this.layoutMode) {
      return;
    }
    this._layoutMode = val;
    setTimeout(() => {
      this.mainWidth = this.calcMainWidth();
      this.mainWidth$.next(this.mainWidth);
    }, 0);
  }

  get layoutMode(): Results.LayoutType {
    return this._layoutMode;
  }

  @Input() checked: boolean;
  @Output() changeSelection = new EventEmitter();

  get showResultSections() {
    return this._showResultSections;
  }

  get model(): T {
    return this._model;
  }
  get selected() {
    return this._selected;
  }

  get location() {
    return this.hubService.currentLocation;
  }

  get resourceId() {
    return this.model.id;
  }

  protected get shareCommandContext(): Commands.Context {
    return {
      searchId: this.searchId,
      resource: { id: this.resourceId, kind: 'link-resource' },
    };
  }

  protected get snippetExtraHeight(): number {
    return 0;
  }

  async isClickable(): Promise<boolean> {
    const isLauncher = await this.hubService.getIsLauncher();
    return this.hubService.currentLocation === 'home' || isLauncher;
  }

  get disablePreviewContextMenu(): boolean {
    return ['cards', 'wiki-collection'].includes(this.pageType);
  }

  confirm$ = new Subject<boolean>();
  confirmOptions: Commands.ConfirmMessageOptions = null;

  shortcutsHandlers: ShortcutHandler[];
  replyDialog = false;
  replyAll = false;
  replyInProgress = false;
  showReply = false;
  fallbackIcon: Results.Icon;
  shortcutAccelerator = isMac() ? 'command' : 'Control';
  contextMenuHelper: ResultsItemContextMenuHelper;
  dragMetadata: DragMetadata;
  dragData: DragData;
  isIconOverlayEmoji: boolean;
  restrictedLink: boolean;
  resourceIcon: Style.Icon;
  appIcon: Style.Icon;
  hoveredIndex: number;
  iconModel: UiIconModel;
  iconOverlayModel: UiIconModel;
  iconIndicateModel: UiIconModel;
  tagGroups: viewTagGroup[] = [];
  moreAttachments: Results.Attachment[];
  ignoreClickOut = false;
  thumbnail: string;
  DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG = Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG;

  readonly SNIPPET_LIMIT = 500;
  readonly INDICATE_PREVIEW_ICON: UiIconModel = {
    type: 'font',
    value: 'icon-eye',
  };
  readonly INDICATE_SUMMARY_ICON: UiIconModel = {
    type: 'font',
    value: 'icon-board-list',
  };
  readonly componentName: string = 'ViewItemComponent';

  private commandBarId = `result-${uuid.v4()}`;
  protected logger: Logger;
  protected _model: T;
  protected prevModel: T;
  protected _selected: boolean;
  protected favoriteOverride: boolean = null;
  private prevSelected: boolean;
  private keyHandlerId: string;
  private onCommandBarSelect: Subscription;
  private registerToCommandBar$ = new Subject<boolean>();
  protected readonly destroy$ = new Subject<boolean>();
  mainWidth: number;
  mainWidth$: ReplaySubject<number> = new ReplaySubject<number>(1);
  afterViewInitPromise: ManualPromise<void> = new ManualPromise();
  bulletsLimit: number;
  tagsGroupLimit: number;
  tagsLimit: number;
  lastChildWidth: number;
  counterBullet: Bullet;
  counterTags = 0;
  showButtonSnippet = false;
  @Output() heightItem = new EventEmitter<number>();
  resultInputTitle: UInputComponent;

  private readonly COUNTER_WIDTH = 15;
  private readonly TAG_MIN_WIDTH = 40;
  private readonly ICONS_WIDTH = 44;
  private readonly TAGS_COUNTER_WIDTH = 12;
  private readonly TAGS_GAP = 18;
  private readonly ATTACHMENTS_GAP = 40;
  private readonly ATTACHMENT_COUNTER_WIDTH = 50;
  private readonly MAX_HEIGHT_SNIPPET = 70;
  private readonly SNIPPET_GAP: number = 8;

  constructor(
    protected hostRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    protected logService: LogService,
    protected applicationsService: ApplicationsService,
    protected contextMenuService: ContextMenuService,
    protected resultCommandBuilder: ResultCommandBuilder,
    protected hubService: HubService,
    protected eventsService: EventsService,
    protected downloadsService: DownloadsService,
    protected resourcesService: ResourcesService,
    private commandsService: CommandsService,
    private commandBarService: CommandBarService,
    protected popupService: PopupService,
    protected keyboardService: KeyboardService,
    protected routerService: RouterService,
    protected errorHandler: GlobalErrorHandler,
    protected resultsService: ResultsService,
    protected flagsService: FlagsService,
    protected favoritesService: FavoritesService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {}

  protected abstract updateThumbnailUrl(): Promise<string>;

  ngOnInit() {
    this.registerToCommandBar$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (v) => (v ? await this.registerToCommandBar() : this.unregisterFromCommandBar()));
    this.logger = this.logService.scope(
      `${this.componentName}- ${this.model?.view?.title?.text?.substring(0, 10) ?? ''}... -${this.model?.id}`
    );
    this.initIcons();
    this.listenToFavoriteChange();
    this.setShowResultSections(this.showResultSectionsDefault, false);
    this.bulletsLimit = this.model?.view?.bullets?.length;
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
      if (this.model?.showResultSections?.showInputTitle) {
        event.stopPropagation();
      }
      this.handleKeys(keys, event);
    }, 100);

    if (this.showResultSections.addCalcBulletsObserver) {
      windowSizeObserver(100)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.onWidthItemChange(this.mainWidth);
        });
    }
  }

  protected handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {}

  initIcons() {
    this.fallbackIcon = this.workspacesService.getLogo();
    this.iconModel = getIconModel(this.model?.view?.icon);
    this.iconOverlayModel = getIconModel(this.model?.view?.iconOverlay);
    this.isIconOverlayEmoji = isIconEmoji((this.model?.view?.icon as Results.Icon)?.lightUrl);
    if (isWikiItem(this.model)) {
      this.iconIndicateModel = getIconModel(this.model.iconIndicate);
    }
    this.tagGroups = this.model?.view?.tagGroups?.map((group) => {
      return {
        tags: group.tags,
        icon: getIconModel(group.icon),
      } as viewTagGroup;
    });
  }

  private onWidthItemChange(prevWidth: number) {
    setTimeout(() => {
      this.mainWidth = this.calcMainWidth();
      if (this.mainWidth === prevWidth || !this.mainWidth) return;
      this.mainWidth$.next(this.mainWidth);
    }, 0);
  }

  protected setShowResultSections(input: Results.ShowResultSections, overrideIfExists = true) {
    this._showResultSections = !this.showResultSections ? {} : this.showResultSections;
    for (const property in input) {
      if (input[property] === undefined || (!overrideIfExists && this._showResultSections[property] !== undefined)) {
        continue;
      }
      this._showResultSections[property] = input[property];
    }
    this.isShowSingleLineView = this._showResultSections?.showSingleLineView || this.model?.showResultSections?.showSingleLineView;
    this.cdr.markForCheck();
  }

  resetShowResultSections() {
    this._showResultSections = {};
  }

  ngAfterViewInit(): void {
    this.afterViewInitPromise.resolve();
    this.initContextMenuHelper();
    this.initContextMenuHelperListeners();
    this.mainWidth = this.calcMainWidth();
    this.mainWidth$.subscribe((_) => {
      this.manageShortView();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.manageShortView();
      }
    });
    this.maxHeightSnippet();
  }

  isPreviewClickAction(action: Action) {
    return isPreviewClickAction(action);
  }

  isAvailableSummary(action: Action) {
    return isEnabledSummary(action);
  }

  private listenToFavoriteChange() {
    this.favoritesService.all$.pipe(untilDestroyed(this)).subscribe((favorites) => {
      if (!favorites) favorites = [];
      const found = favorites?.find((favorite) => {
        if (!favorite?.id || !favorite?.type) return false;
        return this.model.id === favorite.id && convertResultTypeToFavoriteType(this.model.type) === favorite.type;
      });
      this.model.isFavorite = !!found;
      this.cdr.markForCheck();
    });
  }

  maxHeightSnippet() {
    setTimeout(() => {
      const height: number = this.snippetRef?.nativeElement?.offsetHeight;
      this.showButtonSnippet = !this.isLauncher && height >= this.MAX_HEIGHT_SNIPPET;
      const fullHeight = height + this.snippetExtraHeight + this.SNIPPET_GAP;
      this.heightItem.emit(fullHeight);
      this.cdr.markForCheck();
    }, 0);
  }

  async manageShortView() {
    await this.afterViewInitPromise;
    this.bulletsLimit = this.model?.view?.bullets?.length;
    this.tagsGroupLimit = this.tagGroups?.length;
    this.tagsLimit = this.tagGroups?.flatMap((g) => g.tags).length;
    this.moreAttachments = [];
    this.cdr.markForCheck();

    if (this.showResultSections.calcBullets) {
      setTimeout(() => {
        this.setBulletsLimit();
        this.setTagsLimit();
        this.setAttachmentLimit();
      }, 0);
    }
  }

  setCounterBullet() {
    this.counterBullet = {
      parts: [{ text: `+${this.model?.view?.bullets?.length - this.bulletsLimit}`, icon: null, tooltip: '' }],
    };
    const hiddenBullets = this.model?.view?.bullets?.slice(this.bulletsLimit);
    hiddenBullets?.map((b) => b.parts?.map((p) => (this.counterBullet.parts[0].tooltip += ` ${p.text} - ${p.tooltip},`)));
    const endSplit = this.counterBullet.parts[0].tooltip?.length - 1;
    this.counterBullet.parts[0].tooltip = this.counterBullet.parts[0].tooltip.substring(0, endSplit);
  }

  setBulletsLimit() {
    let width = 0;
    this.bulletsLimit = 0;
    for (const i of this.bulletPartsRef?.toArray() || []) {
      this.bulletsLimit++;
      width += i.bulletRef?.nativeElement?.offsetWidth;
      this.lastChildWidth = i.bulletRef?.nativeElement?.offsetWidth - (width - this.mainWidth);
      if (width >= this.mainWidth) break;
    }
    if (this.lastChildWidth < this.TAG_MIN_WIDTH) {
      this.bulletsLimit--;
      this.lastChildWidth = this.mainWidth;
    }
    this.setCounterBullet();
    this.cdr.markForCheck();
  }

  setTagsLimit() {
    if (!this.tagsRef?.toArray()?.length) return;

    const tagsRef = this.tagsRef?.toArray();
    let width = 0;
    this.counterTags = 0;
    let tagsLimit = 0;
    let tagsGroupLimit = 0;
    for (let i = 0; i < tagsRef?.length; i++) {
      const currentTagComp = tagsRef[i];
      width += currentTagComp.tag?.nativeElement?.offsetWidth + this.TAGS_GAP;
      if (width <= this.mainWidth - this.TAGS_COUNTER_WIDTH) {
        if (tagsLimit >= this.tagGroups[tagsGroupLimit]?.tags?.length) {
          tagsGroupLimit++;
          tagsLimit = 1;
        } else {
          tagsLimit++;
        }
      } else {
        this.counterTags++;
      }
    }

    if (this.counterTags) {
      this.tagsLimit = tagsLimit;
      this.tagsGroupLimit = tagsGroupLimit + 1;
    }
    this.cdr.markForCheck();
  }

  setAttachmentLimit() {
    if (!this.attachmentsRef?.toArray()?.length) return;
    const attachmentsRef = this.attachmentsRef?.toArray();
    this.tagsLimit = 0;
    let width = 0;
    attachmentsRef.forEach((attachComponent: ResultAttachmentComponent) => {
      width += attachComponent.attachment?.nativeElement?.offsetWidth + this.ATTACHMENTS_GAP;
      if (width <= this.mainWidth - this.ATTACHMENT_COUNTER_WIDTH) {
        this.tagsLimit++;
      }
    });
    this.moreAttachments = this.tagGroups
      ?.flatMap((g) => g.tags)
      .filter((a) => this.isAttachment(a))
      .slice(this.tagsLimit);
    this.cdr.markForCheck();
  }

  isAttachment(item: any): boolean {
    return isAttachment(item);
  }

  private calcMainWidth() {
    return !this.mainRef ? null : this.mainRef?.nativeElement?.offsetWidth - this.COUNTER_WIDTH - this.ICONS_WIDTH;
  }

  protected initContextMenuHelper(): void {
    this.contextMenuHelper = new ResultsItemContextMenuHelper(
      this.resultCommandBuilder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService
    );
  }

  initContextMenuHelperListeners() {
    this.contextMenuHelper.invoke$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ item, trigger }) => this.onContextMenuCommand(item, trigger));
    this.contextMenuHelper.move$.pipe(takeUntil(this.destroy$)).subscribe(({ event, source }) => this.onRightClick(event, source));
    this.contextMenuHelper.invokeShareMenu$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ item, trigger }) => this.onShareItemClicked(item, trigger));
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
    const ref = this.contextMenuHelper?.ref;
    if (ref) ref.destroy();
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
    this.unregisterFromCommandBar();
  }

  @HostListener('contextmenu', ['$event'])
  onDocumentContextMenu(event: MouseEvent) {
    event.preventDefault();
  }

  @HostListener('auxclick', ['$event']) onAuxClick(event: MouseEvent, source?: ContextMenuInvokeSource) {
    event.preventDefault();

    if (isMiddleClick(event)) return this.onClick(event);
    if (isRightClick(event) && !this.isContainerItem) return this.onRightClick(event, source);
  }

  @HostListener('click', ['$event']) async onClick(event: MouseEvent) {
    const isClickable = await this.isClickable();
    if (this.selected || isClickable) {
      await this.openResourceUrlOrPreview(event);
      if (isClickable) {
        event.preventDefault();
      }
    }
  }

  async onRightClick(event: MouseEvent, source?: ContextMenuInvokeSource) {
    if (this.showResultSections.disableRightClick) {
      return;
    }
    event.preventDefault();
    if (typeof source === 'string' && source !== 'actions') {
      const target = this.sourceToElement(source);
      document.getSelection().selectAllChildren(target);
      event.stopPropagation();
    }
    if (this.showResultSections.rightClickBlock) {
      event.stopPropagation();
    }
    this.openContextMenu(event, source);
  }

  async openContextMenu(event: MouseEvent, source: ContextMenuInvokeSource) {
    const ev: Partial<EventInfo> & { label: ResultContextMenuItemId | 'open_menu' } = {
      label: 'open_menu',
      location: { title: this.getLocation() },
      resources: this.getEventResources(),
    };
    this.sendEvent(ev);
    const itemsToRemove = [];
    if (!this.showResultSections.showFavoriteAction) {
      itemsToRemove.push('favorite');
    }
    const disabledCollections = await this.workspacesService.isFeatureFlagDisabled(Constants.DISABLED_COLLECTIONS_WORKSPACE_FEATURE_FLAG);
    if (!this.showResultSections.showAddToCollectionAction || disabledCollections) {
      itemsToRemove.push('add_to_collection');
    }
    const ref = await this.contextMenuHelper.open(event, this.model, source, null, null, false, true, true, null, false, itemsToRemove);
    ref.close$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextClose.emit();
      this.cdr.markForCheck();
    });

    ref.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextClose.emit();
      this.cdr.markForCheck();
    });
    this.contextOpen.emit();
  }

  private truncateItems() {
    if (!this.resultBulletsRef?.nativeElement) return;
    const containerLength = this.resultBulletsRef.nativeElement.offsetWidth;
    let bulletsLength = 0;
    let hideBullet = false;
    const bullets: HTMLElement[] = [].slice.call(this.resultBulletsRef.nativeElement.children);
    bullets.forEach((b) => {
      bulletsLength += b.offsetWidth ?? 0;
      if (hideBullet) b.style.display = 'none';
      if (bulletsLength >= containerLength) {
        b.style.width = `${containerLength - (bulletsLength - b.offsetWidth)}px`;
        hideBullet = true;
      }
    });
    this.cdr.markForCheck();
  }
  onShareItemClicked(item: ResultContextMenuItem, trigger: TelemetryTrigger = null, source?: string) {
    const location: string = this.getLocation();
    this.commandsService.executeCommand(item.command, this.shareCommandContext);

    this.eventsService.event('results.share_menu', {
      target: 'mouse_click',
      label: item.text,
      location: { title: location },
      resources: this.getEventResources(),
    });
    const command = { ...item.command, type: 'share' };
    const commandData: any = { command, trigger, invokeItem: this.model };
    if (source === 'command-bar') {
      commandData.eventInfo = { jsonData: { 'action.trigger': 'command_bar' } };
    }
    this.onInvoke(commandData);
  }

  protected getLocation() {
    return this.hubService.currentLocation;
  }

  private sourceToElement(source: ContextMenuInvokeSource): HTMLElement {
    switch (source) {
      case 'title': {
        if (!this.titleRef) return;
        return this.titleRef.nativeElement;
      }
      case 'subtitle': {
        if (!this.subtitleRef) return;
        return this.subtitleRef.nativeElement;
      }
      case 'actions': {
        if (!this.actionsRef) return;
        return this.actionsRef.nativeElement;
      }
    }
  }

  async onContextMenuCommand(item: ResultContextMenuItem, trigger: TelemetryTrigger, source?: string) {
    if (isChangeFavoriteStatusCommand(item.command)) {
      this.toggleFavorite(trigger);
    } else {
      const eventInfo: Partial<EventInfo> = {
        target: trigger,
        label: item.command.type,
        location: {
          title: this.hubService.currentLocation,
        },
      };
      await this.onInvoke({ command: item.command, context: item.data, trigger, eventInfo });
    }
    this.contextClose.emit();

    const reportInteractionClick = trigger.includes('click');

    if (!reportInteractionClick) return;

    let label: string = item.id;

    if (label === 'favorite') {
      label = `${this.model.isFavorite ? 'add' : 'remove'}_favorite`; // inverse since we already toggled
    }
    const title = this.getLocation();
    const list = title?.split('/')[0] ? title?.split('/')[0] : null;

    const ev: Partial<EventInfo> = {
      label,
      location: { title },
      resources: this.getEventResources(list),
    };
    this.sendEvent(ev, source === 'preview' ? 'context_menu.open_from_preview' : null);
  }

  protected sendEvent(ev: Partial<EventInfo>, key?: string) {
    this.eventsService.event(key || 'context_menu.open', ev);
  }

  protected getEventResources(list = 'search_bar_results'): EventResource[] {
    return [this.getEventResource(list)];
  }

  protected getEventResource(list = 'search_bar_results'): EventResource {
    return {
      id: this.resourceId,
      list,
      type: this.model.type,
      position: this.resultsIndex,
    };
  }

  isSelectionChanged(): boolean {
    return this._selected !== this.prevSelected;
  }

  onChanges(changes: onChangeMode) {
    if (changes !== 'selected') {
      this.maxHeightSnippet();
      this.initIcons();
      this.setShortcutsHandlers();
    }
    this.recalculateCommandBarRegistrationFromChanges(changes);
    this.cdr.markForCheck();
  }

  recalculateCommandBarRegistrationFromChanges(change: onChangeMode) {
    const modelUpdatedAndNotFirstInit = this._model && this.prevModel;
    if (this._selected && (!this.prevSelected || !this.prevModel || change === 'model' || modelUpdatedAndNotFirstInit)) {
      this.registerToCommandBar$.next(true);
      return;
    }
    const modelChangedOrNotSelected = change === 'model' || (this.prevSelected && !this._selected);
    if (modelChangedOrNotSelected) {
      this.registerToCommandBar$.next(false);
      return;
    }
  }

  @ViewChildren('tooltip') hasToolTip: QueryList<ElementRef>;

  copyUrl(trigger: TelemetryTrigger, eventInfo: Partial<EventInfo>): boolean {
    const isTextSelected = document.getSelection() && document.getSelection().toString() !== '';
    if (!isTextSelected && this.model.view?.title && isOpenUrlCommand(this.model.view.title.onClick)) {
      const command: Commands.CopyClipboard = { type: 'copy-clipboard', value: this.model.view.title.onClick.url };
      this.onInvoke({ command, trigger, eventInfo });
      return true;
    }
    return false;
  }

  protected toggleFavorite(trigger: TelemetryTrigger): void {
    if (!this.workspacesService.isFeatureDisabled(Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG)) {
      this.favoriteOverride = !this._model.isFavorite;
      this.model.favoriteMarkedTime = null;
      this.cdr.markForCheck();
    }
  }

  openUrl(trigger: TelemetryTrigger) {
    if (this.model.view?.title?.onClick) {
      this.onInvoke({
        command: this.model.view?.title?.onClick,
        trigger,
      });
    }
  }

  trackById(_: number, item: any): number {
    return item.$id;
  }

  removeItem(array: Array<any>, id: number): void {
    const index = array.findIndex((p) => p.$id === id);
    if (index < 0) {
      return;
    }
    array.splice(index, 1);
    setTimeout(() => {
      if (this.cdr && !(this.cdr as ViewRef).destroyed) {
        this.cdr.markForCheck();
      }
    });
  }

  onConfirm(confirmed: boolean | null, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
    }
    this.confirmOptions = null;
    this.confirm$.next(confirmed);
    this.cdr.markForCheck();
  }

  async onInvoke(invokeData: InvokeCommand): Promise<void> {
    if (
      (isOpenUrlCommand(invokeData.command) || isOpenFileCommand(invokeData.command)) &&
      !invokeData?.trigger?.includes('context_menu') &&
      getPrimaryActionClick((this.model as SearchResults)?.action) === 'preview' &&
      (invokeData.source === 'title' || (await this.isClickable()))
    ) {
      invokeData.command = this.buildPreviewCommand();
    }
    this.invoke.emit({ ...invokeData, invokerItem: this.model });
  }

  makeContext(...args: any[]) {
    let final = {};
    args.forEach((a) => (final = { ...final, ...a }));
    return final;
  }

  private async openResourceUrlOrPreview(event: MouseEvent) {
    const searchModel: SearchResults = this.model as SearchResults;
    if (getPrimaryActionClick(searchModel?.action) === 'preview') {
      await this.onPreviewClick();
      event?.stopPropagation();
    } else {
      this.openUrl('mouse_click');
    }
  }

  protected async setShortcutsHandlers() {
    const { favorite, copy, openPreview, open } = (await this.keyboardService.loadShortcuts<Shortcuts>(['_home'])) ?? {};
    this.shortcutsHandlers = [];
    const searchModel: SearchResults = this.model as SearchResults;

    const previewable =
      this.isPreviewClickAction(searchModel?.action) &&
      !this.disablePreviewContextMenu &&
      this.showResultSections.showPreviewIconContextMenu;
    if (openPreview && previewable) {
      this.shortcutsHandlers.push({
        fn: (item: ViewItemComponent<T>, trigger: TelemetryTrigger) => this.onPreviewClick(),
        keyBinding: openPreview as KeyName[],
      });
    }
    if (open) {
      const keyOpen = previewable ? open : ['enter'];
      this.shortcutsHandlers.push({
        fn: (item: ViewItemComponent<T>, trigger: TelemetryTrigger) =>
          isWikiCard(searchModel) ? this.onPreviewClick() : item.openUrl(trigger),
        keyBinding: keyOpen as KeyName[],
      });
    }

    if (favorite) {
      this.shortcutsHandlers.push({
        fn: (item: ViewItemComponent<T>) => item.toggleFavorite('keyboard'),
        keyBinding: favorite as KeyName[],
      });
    }

    if (copy) {
      this.shortcutsHandlers.push({
        fn: (item: ViewItemComponent<T>, trigger: TelemetryTrigger, eventInfo) => {
          item.copyUrl(trigger, eventInfo);
        },
        keyBinding: copy as KeyName[],
      });
    }
  }

  async buildPresentableCommands() {
    const params = await this.buildPresentableCommandsParams();
    if (!params) {
      return;
    }
    return await this.resultCommandBuilder.build(params);
  }

  protected async buildPresentableCommandsParams(): Promise<ResultCommandBuilderParams> {
    const isFavorite = this.model?.isFavorite;
    const originTitle = this.model.view?.title;
    if (!originTitle) {
      return;
    }
    const { onClick, text } = originTitle as Results.Title;
    const openUrlCommand: Commands.OpenUrl | null = isOpenUrlCommand(onClick) ? onClick : null;
    const { url, appUri } = openUrlCommand ?? { url: undefined, appUri: undefined };
    return {
      resourceId: this.resourceId,
      open: { appUri, url },
      isFavorite: { isFavorite },
      copy: { text },
      summary: isEnabledSummary((this.model as DisplayItemData)?.action)
        ? { command: this.model.view.title.onClick, resourceId: this.model.id }
        : null,
    };
  }

  protected subscribeOnCommandBarSelect({ item: { command }, via }) {
    if (isShareCommand(command)) {
      this.openSharePopup(via, 'command-bar');
      return;
    } else {
      this.onInvoke({ command, invokerItem: this.model, trigger: via, eventInfo: { jsonData: { 'action.trigger': 'command_bar' } } });
    }
  }

  private async registerToCommandBar() {
    this.unregisterFromCommandBar();

    if (!this.model || (this.showResultSections && !this.showResultSections?.enableCommandBar)) {
      return;
    }
    //HACK - making sure this result is the only one
    this.commandBarService.all
      .map((c) => c.id)
      .filter((id) => id.startsWith('result'))
      .forEach((id) => this.commandBarService.remove(id));

    const children = (await this.buildPresentableCommands())
      ?.filter((c) => c.id !== 'copy')
      .map<SearchPopupItem<'child'>>((item) => ({
        ...item,
        visibility: 'always',
        title: item.text,
        type: 'child',
        icon: item.icon,
        command: item.command,
      }));

    this.onCommandBarSelect = this.commandBarService
      .add({
        id: this.commandBarId,
        type: 'parent',
        title: this._model.view.title.text,
        command: null,
        icon: null,
        visibility: null,
        children,
        data: { sortId: 'COMMANDS' },
      })
      .pipe(takeUntil(this.registerToCommandBar$.pipe(filter((r) => !r))), takeUntil(this.destroy$))
      .subscribe(async ({ item: { command }, via }) => {
        await this.subscribeOnCommandBarSelect({ item: { command }, via });
      });
  }

  protected unregisterFromCommandBar() {
    this.commandBarService.remove(this.commandBarId);
    if (this.onCommandBarSelect) {
      this.onCommandBarSelect.unsubscribe();
      this.onCommandBarSelect = null;
    }
  }

  protected async openSharePopup(via: TelemetryTarget, source?: string) {
    if (!this.selected || !isOpenUrlCommand(this.model.view.title.onClick)) {
      return;
    }
    this.searchPopUpService.placeholder = 'Share via';
    const shareItems = await this.resourcesService.listShareItems(
      this.model.view.title.onClick.url,
      this.resourceId,
      this.model.view.title.text
    );
    const children = shareItems.map<SearchPopupItem<'child'>>(({ icon, id, text, url, desktopUri }) => {
      return {
        id,
        icon: { type: 'img', value: { lightUrl: getShareAssetsPath(icon) } },
        title: text,
        visibility: 'always',
        type: 'child',
        command: { type: 'open-url', url: desktopUri ?? url },
      };
    });
    this.searchPopUpService
      .add({
        id: `share_${this.index}`,
        icon: null,
        command: null,
        visibility: null,
        title: this.model.view.title.text,
        type: 'parent',
        children,
      })
      .pipe(takeUntil(this.searchPopUpService.close$))
      .subscribe(({ item: { command, title } }) => {
        this.onShareItemClicked({ command, text: title, id: 'share' }, 'mouse_click', source);
        this.searchPopUpService.destroy();
      });
    this.searchPopUpService.open(via, 'SharePopup', 'share');

    this.eventsService.event('command_bar.expend_group', {
      location: { title: this.routerService.location },
      label: 'share',
    });
  }

  maxBulletWidth(index: number, bulletPartsRef): number {
    if (!this.mainWidth) return;
    let widthPrevBullets = 0;
    const bulletParts = bulletPartsRef?.toArray();
    for (let i = 0; i < index; i++) {
      widthPrevBullets += bulletParts[i]?.bulletRef.nativeElement?.offsetWidth;
    }
    return this.mainWidth - widthPrevBullets;
  }

  displayInput() {
    if (!this.showResultSections.isEditable) return;
    this.cdr.markForCheck();
  }

  onBlurUInput(inputElement, defaultValue?: string) {
    if (!this.showResultSections.isEditable) return;
    const noValueFromInput = !inputElement?.model?.trim() || !inputElement?.model.length;
    const newTitle = defaultValue && noValueFromInput ? defaultValue : inputElement?.model;
    this.titleChanged.emit(newTitle);
    this.showResultSections.showInputTitle = false;
    this.model.showResultSections.showInputTitle = false;
    this.model.view.title.text = newTitle;
    this.onUpdateTitle();
    this.cdr.markForCheck();
  }

  onSummary(event?: MouseEvent, resourceId?: string) {
    event.stopPropagation();
    this.onInvoke({
      command: {
        type: 'summary',
        command: this.model?.view?.title?.onClick,
        position: event,
        resourceId: resourceId || this.model.id,
        resources: this.getEventResources(),
      } as Commands.Summary,
    });
  }

  onEditTitleClickOut() {
    if (this.resultInputTitle && this.resultInputTitle.model !== this.model.view.title.text) {
      this.onBlurUInput({
        inputElement: this.inputTitleComponent.resultInputTitle,
        defaultInputModel: this.inputTitleComponent.defaultInputModel,
      });
    } else {
      this.model.showResultSections.showInputTitle = false;
      this.showResultSections.showInputTitle = false;
      this.cdr.markForCheck();
    }
  }

  buildPreviewCommand(): Commands.Preview<T> {
    return {
      type: 'preview',
      previewable: this.previewable,
      state: 'popup',
      model: this.model,
      selectedIndex: this.index,
      pageType: this.pageType,
    };
  }

  async onPreviewClick($event?) {
    const ev: Partial<EventInfo> = {
      target: 'preview',
      label: 'open_preview',
      location: { title: this.getLocation() },
      resources: this.getEventResources(),
    };
    if (await this.isClickable()) {
      $event?.stopPropagation();
    }

    this.onInvoke({ command: this.buildPreviewCommand(), trigger: 'mouse_click', eventInfo: ev });
  }

  onChangeModel() {
    return;
  }
  onUpdateTitle() {
    return;
  }
}
